import { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  Landmark,
  Briefcase,
  Target,
  Handshake,
  Check,
  Plus,
  X,
  RotateCcw,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import DealCard, { formatMoney, progressPct, countdownLabel } from "@/components/pharos/DealCard";
import { AssetBanner } from "@/components/pharos/art";
import {
  VEHICLES,
  ASSET_CLASSES,
  SECTORS,
  REGIONS,
  CURRENCIES,
  SIZE_BANDS,
  HIGHLIGHTS,
  MAX_SECTORS,
  MAX_HIGHLIGHTS,
  MAX_TRACK_ROWS,
  MAX_COUNTRIES,
  COUNTRIES,
  countryName,
  labelFor,
} from "@/components/pharos/constants";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

const EMPTY = {
  vehicle: "",
  asset_class: "",
  geography: "",
  currency: "",
  size_band: "",
  total_raise: "",
  amount_closed: "",
  min_ticket: "",
  close_date: "",
  horizon_min_years: "",
  horizon_max_years: "",
  target_irr: "",
  target_moic: "",
  mgmt_fee_pct: "",
  carry_pct: "",
  access_fee_pct: "",
};

const NUMERIC_OPTIONAL = [
  "amount_closed",
  "min_ticket",
  "horizon_min_years",
  "horizon_max_years",
  "target_irr",
  "target_moic",
  "mgmt_fee_pct",
  "carry_pct",
  "access_fee_pct",
];

// Display-only icon per vehicle token.
const VEHICLE_ICONS = { fund: Landmark, spv: Briefcase, direct: Target, co_invest: Handshake };
const VEHICLE_HINTS = {
  fund: "Blind-pool or closed-end fund",
  spv: "Single-asset vehicle",
  direct: "Direct stake in a company",
  co_invest: "Alongside a lead sponsor",
};

const RAISE_QUICK = [5e6, 10e6, 25e6, 50e6, 100e6];
const TICKET_QUICK = [100e3, 250e3, 500e3, 1e6];
const CLOSED_QUICK = [25, 50, 75, 100];

const labelCls = "text-sm font-medium text-gray-700";
const boxCls =
  "rounded-md border border-input bg-background px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-[#C41E2A]/30";

// Builds the JSON body from the form state. Empty optional fields are omitted;
// track_record is sent only for vehicle=fund. Exported for the admin form's
// twin and for tests.
export function buildListingPayload(f, sectors, highlights, track, countries = []) {
  const body = {
    vehicle: f.vehicle,
    asset_class: f.asset_class,
    sectors,
    geography: f.geography,
    currency: f.currency,
    total_raise: Number(f.total_raise),
    close_date: f.close_date,
    highlights,
  };
  // Only allowlisted ISO codes are ever sent; typed search text never reaches here.
  const codes = countries.filter((c) => COUNTRIES.includes(c));
  if (codes.length) body.countries = codes;
  if (f.size_band) body.size_band = f.size_band;
  for (const k of NUMERIC_OPTIONAL) {
    if (f[k] !== "") body[k] = Number(f[k]);
  }
  if (f.vehicle === "fund") {
    body.track_record = track
      .filter((r) => r.fund_number !== "" || r.dpi !== "" || r.net_irr !== "")
      .map((r) => ({
        fund_number: Number(r.fund_number),
        dpi: Number(r.dpi),
        net_irr: Number(r.net_irr),
      }));
  }
  return body;
}

const str = (v) => (v === null || v === undefined ? "" : String(v));

// Maps an existing public deal (or its pending_revision values) back to form
// state. Set numeric values become strings so sliders count as "set".
export function listingToForm(l) {
  const f = { ...EMPTY };
  for (const k of Object.keys(EMPTY)) f[k] = str(l?.[k]);
  return {
    f,
    sectors: Array.isArray(l?.sectors) ? [...l.sectors] : [],
    highlights: Array.isArray(l?.highlights) ? [...l.highlights] : [],
    countries: Array.isArray(l?.countries) ? [...l.countries] : [],
    track: (Array.isArray(l?.track_record) ? l.track_record : []).map((r) => ({
      fund_number: str(r.fund_number),
      dpi: str(r.dpi),
      net_irr: str(r.net_irr),
    })),
  };
}

// Local calendar date as YYYY-MM-DD (not UTC, so "today" matches the user's).
function localISO(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return localISO(d);
}

function suggestBand(raise) {
  const n = Number(raise);
  if (!(n > 0)) return "";
  if (n < 10e6) return "lt_10m";
  if (n < 50e6) return "m10_50";
  if (n < 250e6) return "m50_250";
  if (n < 1e9) return "m250_1b";
  return "b1_plus";
}

function quickLabel(n) {
  if (n >= 1e6) return `${n / 1e6}M`;
  return `${n / 1e3}k`;
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function Chip({ selected, disabled, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={
        "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40 " +
        (selected
          ? "border-[#C41E2A] bg-[#C41E2A] text-white shadow-sm"
          : "border-gray-200 bg-white text-gray-700 hover:border-[#C41E2A]/50 hover:bg-[rgba(196,30,42,0.08)]") +
        (disabled ? " opacity-40 cursor-not-allowed hover:border-gray-200 hover:bg-white" : "")
      }
    >
      {selected && <Check className="h-3 w-3" aria-hidden="true" />}
      {children}
    </button>
  );
}

function QuickChip({ onClick, children, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs text-gray-600 hover:border-[#C41E2A] hover:text-[#C41E2A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40"
    >
      {children}
    </button>
  );
}

// Multi-select chip group with an n/max counter. Extra chips disable at max.
function ChipGroup({ legend, options, selected, max, onToggle, required }) {
  return (
    <fieldset className="space-y-2">
      <legend className={labelCls}>
        {legend}
        {required && <span className="text-[#C41E2A]"> *</span>}{" "}
        <span
          className={"ml-1 rounded-full px-2 py-0.5 text-xs tabular-nums " + (selected.length >= max ? "bg-[#C41E2A] text-white" : "bg-gray-100 text-gray-500")}
        >
          {selected.length}/{max}
        </span>
      </legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = selected.includes(o.value);
          return (
            <Chip key={o.value} selected={on} disabled={!on && selected.length >= max} onClick={() => onToggle(o.value)}>
              {o.label}
            </Chip>
          );
        })}
      </div>
    </fieldset>
  );
}

// Single-select radiogroup. `renderOption` draws the inner content; clicking
// the selected option again clears it when `clearable`.
function RadioGroup({ label, options, value, onChange, className, optionClass, renderOption, clearable }) {
  return (
    <div role="radiogroup" aria-label={label} className={className}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={o.label}
            onClick={() => onChange(on && clearable ? "" : o.value)}
            className={
              "transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40 " +
              optionClass +
              " " +
              (on
                ? "border-[#C41E2A] bg-[rgba(196,30,42,0.08)] text-[#C41E2A] ring-1 ring-[#C41E2A]"
                : "border-gray-200 bg-white text-gray-700 hover:border-[#C41E2A]/50")
            }
          >
            {renderOption(o, on)}
          </button>
        );
      })}
    </div>
  );
}

// Searchable picker over the FIXED country list. The typed text only filters
// the visible options; the value handed to onAdd is always an allowlisted code.
// Not inside any form element, never part of the payload.
const COUNTRY_OPTIONS = COUNTRIES.map((code) => ({ code, name: countryName(code) })).sort((a, b) =>
  a.name.localeCompare(b.name)
);

function CountryPicker({ selected, max, onAdd, onRemove }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const full = selected.length >= max;
  const q = query.trim().toLowerCase();
  const matches = COUNTRY_OPTIONS.filter(
    (o) => !selected.includes(o.code) && (!q || o.name.toLowerCase().includes(q) || o.code.toLowerCase() === q)
  ).slice(0, 60);
  const add = (code) => {
    onAdd(code);
    setQuery("");
  };
  return (
    <div className="space-y-2" ref={rootRef}>
      <p className={labelCls}>
        Countries{" "}
        <span
          className={"ml-1 rounded-full px-2 py-0.5 text-xs tabular-nums " + (full ? "bg-[#C41E2A] text-white" : "bg-gray-100 text-gray-500")}
        >
          {selected.length}/{max}
        </span>{" "}
        <span className="text-xs font-normal text-gray-400">optional</span>
      </p>
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Selected countries">
          {selected.map((c) => (
            <li key={c}>
              <button
                type="button"
                aria-label={`Remove ${countryName(c)}`}
                onClick={() => onRemove(c)}
                className="inline-flex items-center gap-1 rounded-full border border-[#C41E2A] bg-[#C41E2A] px-3 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40"
              >
                {countryName(c)}
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Command
        shouldFilter={false}
        label="Search countries"
        className="h-auto max-w-sm overflow-visible rounded-md border border-input bg-background"
        onBlur={(e) => {
          if (!rootRef.current?.contains(e.relatedTarget)) setOpen(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
      >
        <CommandInput
          aria-label="Search countries"
          placeholder={full ? `Maximum of ${max} countries` : "Search countries to add"}
          value={query}
          disabled={full}
          onValueChange={(v) => {
            setQuery(v);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && !full && (
          <CommandList className="max-h-56 border-t">
            <CommandEmpty>No matching country</CommandEmpty>
            <CommandGroup>
              {matches.map((o) => (
                <CommandItem key={o.code} value={o.code} onSelect={() => add(o.code)} onMouseDown={(e) => e.preventDefault()}>
                  {o.name}
                  <span className="ml-auto text-xs text-gray-400">{o.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        )}
      </Command>
    </div>
  );
}

function NumberBox({ id, label, value, onChange, step = "any", min, max, className = "w-24" }) {
  return (
    <input
      id={id}
      aria-label={label}
      type="number"
      inputMode="decimal"
      step={step}
      min={min}
      max={max}
      className={boxCls + " " + className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Optional-number slider. Untouched => value stays "" (omitted from payload).
// Moving the slider or typing in the box sets it; "Clear" resets to unset.
function SliderField({ id, label, unit, value, onChange, sliderMin = 0, sliderMax, step, boxMin, boxMax }) {
  const isSet = value !== "";
  const pos = isSet ? Math.min(sliderMax, Math.max(sliderMin, Number(value) || 0)) : sliderMin;
  return (
    <div className="space-y-1.5 rounded-lg border border-gray-100 bg-gray-50/50 p-3">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className={labelCls}>
          {label}
        </label>
        <div className="flex items-center gap-1.5">
          {isSet ? (
            <button
              type="button"
              aria-label={`Clear ${label}`}
              onClick={() => onChange("")}
              className="rounded p-1 text-gray-400 hover:text-[#C41E2A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          ) : (
            <span className="text-xs italic text-gray-400">Not set</span>
          )}
          <NumberBox id={id} label={label} value={value} onChange={onChange} step={step} min={boxMin} max={boxMax} className="w-20" />
          <span className="w-4 text-xs text-gray-500">{unit}</span>
        </div>
      </div>
      <SliderPrimitive.Root
        className="relative flex h-5 w-full touch-none select-none items-center"
        min={sliderMin}
        max={sliderMax}
        step={step === "any" ? 0.1 : Number(step)}
        value={[pos]}
        onValueChange={([v]) => onChange(String(Number(v.toFixed(2))))}
      >
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-gray-200">
          <SliderPrimitive.Range className={"absolute h-full " + (isSet ? "bg-[#C41E2A]" : "bg-gray-300")} />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label={`${label} slider`}
          className={
            "block h-4 w-4 rounded-full border-2 bg-white shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40 " +
            (isSet ? "border-[#C41E2A]" : "border-gray-300")
          }
        />
      </SliderPrimitive.Root>
    </div>
  );
}

// Dual-thumb horizon range. Both ends unset until the user touches it.
function HorizonField({ min, max, onChange }) {
  const isSet = min !== "" || max !== "";
  const lo = min === "" ? 0 : Math.min(30, Math.max(0, Number(min) || 0));
  const hi = max === "" ? 30 : Math.min(30, Math.max(0, Number(max) || 0));
  return (
    <div className="space-y-1.5 rounded-lg border border-gray-100 bg-gray-50/50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={labelCls}>Horizon (years)</span>
        <div className="flex items-center gap-1.5">
          {isSet ? (
            <button
              type="button"
              aria-label="Clear Horizon"
              onClick={() => onChange("", "")}
              className="rounded p-1 text-gray-400 hover:text-[#C41E2A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          ) : (
            <span className="text-xs italic text-gray-400">Not set</span>
          )}
          <NumberBox id="pl-hmin" label="Horizon min (years)" step="1" min="0" max="30" value={min} onChange={(v) => onChange(v, max)} className="w-16" />
          <span className="text-xs text-gray-400">to</span>
          <NumberBox id="pl-hmax" label="Horizon max (years)" step="1" min="0" max="30" value={max} onChange={(v) => onChange(min, v)} className="w-16" />
        </div>
      </div>
      <SliderPrimitive.Root
        className="relative flex h-5 w-full touch-none select-none items-center"
        min={0}
        max={30}
        step={1}
        minStepsBetweenThumbs={0}
        value={[lo, hi]}
        onValueChange={([a, b]) => onChange(String(a), String(b))}
      >
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-gray-200">
          <SliderPrimitive.Range className={"absolute h-full " + (isSet ? "bg-[#C41E2A]" : "bg-gray-300")} />
        </SliderPrimitive.Track>
        {["Horizon min slider", "Horizon max slider"].map((l) => (
          <SliderPrimitive.Thumb
            key={l}
            aria-label={l}
            className={
              "block h-4 w-4 rounded-full border-2 bg-white shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40 " +
              (isSet ? "border-[#C41E2A]" : "border-gray-300")
            }
          />
        ))}
      </SliderPrimitive.Root>
      <div className="flex justify-between text-[10px] text-gray-400 tabular-nums">
        <span>0</span>
        <span>10</span>
        <span>20</span>
        <span>30 yrs</span>
      </div>
    </div>
  );
}

function MoneyField({ id, label, value, onChange, currency, quick, quickLabelFn, required }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className={labelCls}>
          {label}
          {required && <span className="text-[#C41E2A]"> *</span>}
        </label>
        <span className="text-sm font-semibold tabular-nums text-[#C41E2A]">{formatMoney(value, currency || "USD")}</span>
      </div>
      <input
        id={id}
        aria-label={label}
        type="number"
        inputMode="decimal"
        step="any"
        min="0"
        className={boxCls + " w-full py-2"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="flex flex-wrap gap-1.5">
        {quick.map((q) => (
          <QuickChip key={q} onClick={() => onChange(String(q))} label={`Set ${label} to ${quickLabelFn(q)}`}>
            {quickLabelFn(q)}
          </QuickChip>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

const STEPS = [
  { n: 1, label: "What are you listing", title: "What are you listing?", hint: "Pick the structure of the deal." },
  { n: 2, label: "The deal", title: "The deal", hint: "Asset class, sectors and where it invests." },
  { n: 3, label: "Amount", title: "The raise", hint: "Currency, size and how much is committed." },
  { n: 4, label: "Targets", title: "Targets & terms", hint: "All optional. Leave a slider alone and it is not sent." },
  { n: 5, label: "Highlights", title: "Highlights", hint: "Up to three signals that make this deal stand out." },
  { n: 6, label: "Review", title: "Review & submit", hint: "This is exactly what buyers will see." },
];
const LAST = STEPS.length;
const SERIF = { fontFamily: "'Playfair Display', Georgia, serif" };

function Stepper({ step, canReach, onGo }) {
  const fill = ((step - 1) / (LAST - 1)) * 100;
  return (
    <nav aria-label="Listing steps" className="relative">
      {/* connector spans centre of first to centre of last column */}
      <div className="absolute top-[15px] h-0.5 bg-gray-200" style={{ left: `${50 / LAST}%`, right: `${50 / LAST}%` }} aria-hidden="true">
        <div
          data-testid="stepper-fill"
          className="h-full bg-[#C41E2A] transition-[width] duration-500 ease-out motion-reduce:transition-none"
          style={{ width: `${fill}%` }}
        />
      </div>
      <ol className="relative grid" style={{ gridTemplateColumns: `repeat(${LAST}, minmax(0, 1fr))` }}>
        {STEPS.map((s) => {
          const completed = s.n < step;
          const current = s.n === step;
          const clickable = !current && canReach(s.n);
          return (
            <li key={s.n} className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                aria-label={`Step ${s.n}: ${s.label}`}
                aria-current={current ? "step" : undefined}
                disabled={!clickable}
                onClick={() => onGo(s.n)}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-all duration-300 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40 " +
                  (completed
                    ? "bg-[#C41E2A] text-white"
                    : current
                      ? "scale-110 border-2 border-[#C41E2A] bg-white text-[#C41E2A] ring-4 ring-[#C41E2A]/15"
                      : "bg-gray-200 text-gray-500") +
                  (clickable ? " cursor-pointer hover:brightness-95" : " cursor-default")
                }
              >
                {completed ? <Check className="h-4 w-4" aria-hidden="true" /> : s.n}
              </button>
              <span
                aria-hidden="true"
                className={"text-center text-[11px] leading-tight sm:text-xs " + (current ? "font-bold text-gray-900" : completed ? "text-gray-700" : "text-gray-400")}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function NewListingDialog({
  open,
  onOpenChange,
  onCreated,
  termsAccepted = true,
  onTermsRequired,
  listing = null,
  mode = "create",
}) {
  const editing = mode === "edit" && !!listing;
  const [f, setF] = useState(EMPTY);
  const [sectors, setSectors] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [track, setTrack] = useState([]);
  const [countries, setCountries] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState("fwd");

  // Edit mode: prefill from the listing and start at step 2.
  useEffect(() => {
    if (!open || !editing) return;
    const init = listingToForm(listing);
    setF(init.f);
    setSectors(init.sectors);
    setHighlights(init.highlights);
    setTrack(init.track);
    setCountries(init.countries);
    setStep(2);
    setDir("fwd");
  }, [open, editing, listing]);

  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const toggle = (setter, max) => (v) =>
    setter((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : prev.length >= max ? prev : [...prev, v]));
  const setTrackCell = (i, k, v) =>
    setTrack((prev) => prev.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const today = localISO(new Date());

  const checks = {
    vehicle: !!f.vehicle,
    asset_class: !!f.asset_class,
    sectors: sectors.length >= 1,
    geography: !!f.geography,
    currency: !!f.currency,
    total_raise: Number(f.total_raise) > 0,
    close_date: !!f.close_date,
  };
  const doneCount = Object.values(checks).filter(Boolean).length;
  const totalReq = Object.keys(checks).length;
  const complete = doneCount === totalReq;
  const stepOk = (n) => {
    if (n === 1) return checks.vehicle;
    if (n === 2) return checks.asset_class && checks.sectors && checks.geography;
    if (n === 3) return checks.currency && checks.total_raise && checks.close_date;
    return true;
  };
  // A step is reachable when every earlier required step is complete.
  const canReach = (n) => {
    for (let k = 1; k < n; k++) if (!stepOk(k)) return false;
    return true;
  };
  const goTo = (n) => {
    setDir(n < step ? "back" : "fwd");
    setStep(n);
  };
  const next = () => step < LAST && stepOk(step) && goTo(step + 1);
  const back = () => step > 1 && goTo(step - 1);
  const suggested = suggestBand(f.total_raise);

  // Live preview: same title format as backend deal_title(); id is a placeholder.
  const previewDeal = useMemo(() => {
    const parts = [
      labelFor(VEHICLES, f.vehicle),
      labelFor(ASSET_CLASSES, f.asset_class),
      sectors.length ? labelFor(SECTORS, sectors[0]) : "",
      labelFor(REGIONS, f.geography),
    ].filter(Boolean);
    const body = buildListingPayload(f, sectors, highlights, track, countries);
    return {
      ...body,
      size_band: f.size_band,
      title: parts.length ? parts.join(" · ") : "Your listing",
      total_raise: f.total_raise === "" ? "" : Number(f.total_raise),
    };
  }, [f, sectors, highlights, track, countries]);

  function handleClose(isOpen) {
    if (!isOpen) {
      setF(EMPTY);
      setSectors([]);
      setHighlights([]);
      setTrack([]);
      setCountries([]);
      setStep(1);
      setDir("fwd");
    }
    onOpenChange(isOpen);
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!termsAccepted) {
      onTermsRequired?.();
      return;
    }
    if (!f.vehicle || !f.asset_class || !f.geography || !f.currency || !f.close_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (sectors.length < 1) {
      toast.error("Select at least one sector");
      return;
    }
    if (!(Number(f.total_raise) > 0)) {
      toast.error("Total raise must be greater than zero");
      return;
    }
    setSubmitting(true);
    try {
      const body = buildListingPayload(f, sectors, highlights, track, countries);
      if (editing) {
        const res = await api.put(`/pharos/listings/${listing.id}`, body);
        const result = res?.data?.result;
        if (result === "revision_pending") {
          toast.success("Changes sent for review. Your live listing stays up until approved.");
        } else if (result === "resubmitted") {
          toast.success("Resubmitted for review");
        } else {
          toast.success("Listing updated — pending review");
        }
      } else {
        await api.post("/pharos/listings", body);
        toast.success("Submitted for review");
      }
      handleClose(false);
      onCreated?.();
    } catch (err) {
      if (err?.response?.data?.detail === "terms_not_accepted") {
        toast.error("Please accept the Pharos terms first.");
        onTermsRequired?.();
      } else {
        toast.error(getErrorMessage(err, "Failed to submit listing"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const pct = progressPct(f.amount_closed, f.total_raise);
  const countdown = countdownLabel(f.close_date);
  const cur = STEPS[step - 1];
  const showAside = step >= 2 && step <= 5;
  const optional = step === 4 || step === 5;
  const anim =
    "animate-in fade-in duration-300 motion-reduce:animate-none " +
    (dir === "fwd" ? "slide-in-from-right-6" : "slide-in-from-left-6");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={
          (showAside ? "sm:max-w-6xl" : "sm:max-w-4xl") +
          " flex h-[92vh] max-h-[92vh] flex-col gap-0 overflow-hidden p-0"
        }
      >
        {/* Fixed header: never scrolls */}
        <div className="shrink-0 space-y-4 px-6 pb-4 pt-6">
        <DialogHeader>
          <DialogTitle style={SERIF} className="text-2xl">
            {editing ? "Edit listing" : "New listing"}
          </DialogTitle>
          <DialogDescription>
            Structured details only. Elementar reviews every listing before it is published, and your
            identity is never shown to buyers.
          </DialogDescription>
        </DialogHeader>

        <Stepper step={step} canReach={canReach} onGo={goTo} />
        </div>

        {/* Body: the only scroller. Footer is a sibling, so nothing renders under it. */}
        <div className="min-h-0 flex-1 overflow-y-auto border-t border-gray-100 px-6 pb-10 pt-5">
        <div className={showAside ? "grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]" : ""}>
          <div key={step} className={"min-w-0 space-y-5 " + anim}>
            <div>
              <h3 className="text-2xl font-semibold text-gray-900" style={SERIF}>
                {cur.title}
              </h3>
              <p className="mt-1 text-sm text-gray-500">{cur.hint}</p>
            </div>

            {step === 1 && (
              <div role="radiogroup" aria-label="What are you listing?" className="grid grid-cols-2 gap-5 sm:grid-cols-4">
                {VEHICLES.map((o) => {
                  const Icon = VEHICLE_ICONS[o.value];
                  const on = f.vehicle === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      aria-label={o.label}
                      onClick={() => set("vehicle")(o.value)}
                      className={
                        "group flex flex-col items-center gap-3 min-h-[13rem] justify-center rounded-2xl border px-5 py-8 text-center transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E2A]/40 " +
                        (on ? "border-[#C41E2A] bg-[rgba(196,30,42,0.08)] ring-1 ring-[#C41E2A]" : "border-gray-200 bg-white hover:border-[#C41E2A]")
                      }
                    >
                      <span
                        className={
                          "flex h-16 w-16 items-center justify-center rounded-full transition-colors " +
                          (on ? "bg-[#C41E2A] text-white" : "bg-[rgba(196,30,42,0.08)] text-[#C41E2A] group-hover:bg-[#C41E2A] group-hover:text-white")
                        }
                      >
                        <Icon className="h-8 w-8" aria-hidden="true" />
                      </span>
                      <span className="text-lg font-semibold text-gray-900">{o.label}</span>
                      <span className="text-sm leading-snug text-gray-500">{VEHICLE_HINTS[o.value]}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <>
                <div className="space-y-2">
                  <p className={labelCls}>
                    Asset class <span className="text-[#C41E2A]">*</span>
                  </p>
                  <RadioGroup
                    label="Asset class"
                    options={ASSET_CLASSES}
                    value={f.asset_class}
                    onChange={set("asset_class")}
                    className="grid grid-cols-2 gap-3 sm:grid-cols-3"
                    optionClass="overflow-hidden rounded-xl border p-0 text-left"
                    renderOption={(o) => (
                      <AssetBanner assetClass={o.value} className="w-full h-16">
                        <div className="flex h-full items-end p-2">
                          <span className="text-sm font-semibold text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.7)]">{o.label}</span>
                        </div>
                      </AssetBanner>
                    )}
                  />
                </div>
                <ChipGroup legend="Sectors" required options={SECTORS} selected={sectors} max={MAX_SECTORS} onToggle={toggle(setSectors, MAX_SECTORS)} />
                <div className="space-y-2">
                  <p className={labelCls}>
                    Geography <span className="text-[#C41E2A]">*</span>
                  </p>
                  <RadioGroup
                    label="Geography"
                    options={REGIONS}
                    value={f.geography}
                    onChange={set("geography")}
                    className="flex flex-wrap gap-2"
                    optionClass="rounded-full border px-3 py-1 text-sm"
                    renderOption={(o) => o.label}
                  />
                </div>
                <CountryPicker
                  selected={countries}
                  max={MAX_COUNTRIES}
                  onAdd={(c) => setCountries((prev) => (prev.length >= MAX_COUNTRIES || prev.includes(c) || !COUNTRIES.includes(c) ? prev : [...prev, c]))}
                  onRemove={(c) => setCountries((prev) => prev.filter((x) => x !== c))}
                />
              </>
            )}

            {step === 3 && (
              <>
                <div className="space-y-2">
                  <p className={labelCls}>
                    Currency <span className="text-[#C41E2A]">*</span>
                  </p>
                  <RadioGroup
                    label="Currency"
                    options={CURRENCIES}
                    value={f.currency}
                    onChange={set("currency")}
                    className="inline-flex overflow-hidden rounded-lg border border-gray-200"
                    optionClass="border-0 border-r last:border-r-0 px-4 py-1.5 text-sm font-medium rounded-none"
                    renderOption={(o) => o.label}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <MoneyField id="pl-raise" label="Total raise" required value={f.total_raise} onChange={set("total_raise")} currency={f.currency} quick={RAISE_QUICK} quickLabelFn={quickLabel} />
                  <MoneyField id="pl-closed" label="Amount closed" value={f.amount_closed} onChange={set("amount_closed")} currency={f.currency} quick={[]} quickLabelFn={quickLabel} />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                  <span>Set closed as share of raise:</span>
                  {CLOSED_QUICK.map((q) => (
                    <QuickChip
                      key={q}
                      label={`Closed ${q} percent of raise`}
                      onClick={() => Number(f.total_raise) > 0 && set("amount_closed")(String(Math.round((Number(f.total_raise) * q) / 100)))}
                    >
                      {q}%
                    </QuickChip>
                  ))}
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-xs text-gray-600">
                    <span>
                      {formatMoney(f.amount_closed || 0, f.currency || "USD")} of {formatMoney(f.total_raise || 0, f.currency || "USD")} closed
                    </span>
                    <span className="tabular-nums">{pct}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
                    <div className="h-full rounded-full bg-[#C41E2A] transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>

                <MoneyField id="pl-ticket" label="Min ticket" value={f.min_ticket} onChange={set("min_ticket")} currency={f.currency} quick={TICKET_QUICK} quickLabelFn={quickLabel} />

                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <p className={labelCls}>Size band</p>
                    {suggested && f.size_band !== suggested && (
                      <button type="button" onClick={() => set("size_band")(suggested)} className="text-xs text-[#C41E2A] underline underline-offset-2">
                        Suggested: {labelFor(SIZE_BANDS, suggested)} - use
                      </button>
                    )}
                  </div>
                  <RadioGroup
                    label="Size band"
                    options={SIZE_BANDS}
                    value={f.size_band}
                    onChange={set("size_band")}
                    clearable
                    className="grid grid-cols-5 gap-0 overflow-hidden rounded-lg border border-gray-200"
                    optionClass="border-0 border-r last:border-r-0 rounded-none px-1 py-3 text-center text-xs font-medium sm:text-sm"
                    renderOption={(o, on) => (
                      <span className="flex flex-col items-center gap-1">
                        <span className={"h-1.5 w-full rounded-full " + (on ? "bg-[#C41E2A]" : "bg-gray-200")} />
                        {o.label}
                      </span>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="pl-close" className={labelCls}>
                      Close date <span className="text-[#C41E2A]">*</span>
                    </label>
                    {countdown && <span className="text-sm font-semibold text-[#C41E2A]">{countdown}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id="pl-close"
                      aria-label="Close date"
                      type="date"
                      min={today}
                      className={boxCls + " py-2"}
                      value={f.close_date}
                      onChange={(e) => set("close_date")(e.target.value)}
                    />
                    {[30, 60, 90].map((d) => (
                      <QuickChip key={d} label={`Close in ${d} days`} onClick={() => set("close_date")(addDaysISO(d))}>
                        +{d}d
                      </QuickChip>
                    ))}
                  </div>
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <SliderField id="pl-irr" label="Target IRR (%)" unit="%" value={f.target_irr} onChange={set("target_irr")} sliderMax={50} step="0.5" boxMin="-100" boxMax="200" />
                  <SliderField id="pl-moic" label="Target MOIC (x)" unit="x" value={f.target_moic} onChange={set("target_moic")} sliderMax={5} step="0.1" boxMin="0" boxMax="20" />
                  <SliderField id="pl-mgmt" label="Mgmt fee (%)" unit="%" value={f.mgmt_fee_pct} onChange={set("mgmt_fee_pct")} sliderMax={10} step="0.1" boxMin="0" boxMax="10" />
                  <SliderField id="pl-carry" label="Carry (%)" unit="%" value={f.carry_pct} onChange={set("carry_pct")} sliderMax={30} step="1" boxMin="0" boxMax="50" />
                  <SliderField id="pl-access" label="Access fee (%)" unit="%" value={f.access_fee_pct} onChange={set("access_fee_pct")} sliderMax={10} step="0.1" boxMin="0" boxMax="10" />
                </div>
                <HorizonField
                  min={f.horizon_min_years}
                  max={f.horizon_max_years}
                  onChange={(a, b) => setF((p) => ({ ...p, horizon_min_years: a, horizon_max_years: b }))}
                />
              </>
            )}

            {step === 5 && (
              <>
                <ChipGroup legend="Highlights" options={HIGHLIGHTS} selected={highlights} max={MAX_HIGHLIGHTS} onToggle={toggle(setHighlights, MAX_HIGHLIGHTS)} />

                {f.vehicle === "fund" && (
                  <fieldset className="space-y-2">
                    <legend className={labelCls}>Track record (prior funds)</legend>
                    {track.map((r, i) => (
                      <div key={i} className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-900 text-xs font-semibold text-white" aria-hidden="true">
                          {i + 1}
                        </span>
                        {[
                          ["fund_number", "Fund #", "1", "1", "50"],
                          ["dpi", "DPI (x)", "any", "0", "20"],
                          ["net_irr", "Net IRR (%)", "any", "-100", "200"],
                        ].map(([k, lab, st, min, max]) => (
                          <div key={k} className="space-y-1">
                            <label htmlFor={`pl-tr-${k}-${i}`} className="block text-xs font-medium text-gray-600">
                              {lab}
                            </label>
                            <NumberBox id={`pl-tr-${k}-${i}`} label={lab} step={st} min={min} max={max} value={r[k]} onChange={(v) => setTrackCell(i, k, v)} className="w-24" />
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" aria-label={`Remove fund ${i + 1}`} onClick={() => setTrack((p) => p.filter((_, j) => j !== i))}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {track.length < MAX_TRACK_ROWS && (
                      <Button type="button" variant="outline" size="sm" onClick={() => setTrack((p) => [...p, { fund_number: "", dpi: "", net_irr: "" }])}>
                        <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                        Add fund
                      </Button>
                    )}
                  </fieldset>
                )}
              </>
            )}

            {step === 6 && (
              <div className="mx-auto max-w-xl">
                <DealCard deal={previewDeal} large />
              </div>
            )}
          </div>

          {showAside && (
            <aside className="hidden space-y-3 lg:sticky lg:top-0 lg:block lg:self-start" aria-label="Live preview">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Live preview</p>
              <DealCard deal={previewDeal} />
              <p className="text-[11px] text-gray-400">This is how buyers will see the listing.</p>
            </aside>
          )}
        </div>
        </div>

        {/* Footer: normal flex child below the scrolling body */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 bg-white px-6 py-3">
          <div className="w-24">
            {step > 1 && (
              <Button type="button" variant="outline" onClick={back} disabled={submitting}>
                Back
              </Button>
            )}
          </div>
          <div className="flex flex-col items-center" data-testid="completion">
            <span className="text-sm font-medium text-gray-700">
              Step {step} of {LAST}
            </span>
            <span className="text-[11px] tabular-nums text-gray-400">{doneCount}/{totalReq} required</span>
          </div>
          <div className="flex items-center justify-end gap-3">
            {optional && (
              <button type="button" onClick={() => goTo(step + 1)} className="text-sm text-gray-500 underline underline-offset-2">
                Skip
              </button>
            )}
            {step < LAST ? (
              <Button
                type="button"
                onClick={next}
                disabled={!stepOk(step)}
                className="text-white"
                style={{ backgroundColor: BRAND.bg }}
              >
                Next step {"→"}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                aria-disabled={!complete}
                className={"text-white transition-opacity " + (complete ? "" : "opacity-50")}
                style={{ backgroundColor: BRAND.bg }}
              >
                {submitting ? "Submitting..." : editing ? "Submit changes for review" : "Submit for review"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
